import { z } from 'zod'

import { pruned } from '@/lib/filters'

/** Where an order line stands against what it was paid, as the API names it. */
export const RECONCILIATION_STATUSES = [
  'matched',
  'unmatched',
  'partially_matched',
  'returned',
] as const
export type ReconciliationStatusKey = (typeof RECONCILIATION_STATUSES)[number]

export const RECONCILIATION_PAGE_SIZES = [10, 25, 50, 100] as const
export const DEFAULT_RECONCILIATION_PAGE_SIZE = 50

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const ids = z.array(z.number().int().positive()).max(50).optional().catch(undefined)

/**
 * Payment Reconciliation's URL: the reports' scope (warehouses, the
 * marketplace, its stores, brands) and the range; whether the report has
 * been generated for them; the status the cards narrow to; the search, the
 * sort and the page. Every field optional; defaults apply at read time.
 */
export const reconciliationSearchSchema = z.object({
  warehouseIds: ids.describe('Warehouses to read, by id; empty means every warehouse.'),
  marketplaceId: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The marketplace to read, by id; absent means every marketplace.'),
  storeIds: ids.describe('Stores to include, by id; empty means every store.'),
  brandIds: ids.describe('Brands to include, by id; empty means every brand.'),
  dateFrom: day
    .optional()
    .catch(undefined)
    .describe('First day of the range, YYYY-MM-DD; the last 30 days when absent.'),
  dateTo: day
    .optional()
    .catch(undefined)
    .describe('Last day of the range, YYYY-MM-DD, inclusive; today when absent.'),
  // Set by Generate Report: nothing loads until it is.
  go: z
    .literal(1)
    .optional()
    .catch(undefined)
    .describe(
      '1 once Generate report is pressed; nothing loads without it. Any filter or date change clears it.',
    ),
  status: z
    .enum(RECONCILIATION_STATUSES)
    .optional()
    .catch(undefined)
    .describe('Show only order lines with this reconciliation status; absent shows every line.'),
  q: z
    .string()
    .max(100)
    .optional()
    .catch(undefined)
    .describe('Search text over the rows (SKU code, name, brand or store).'),
  sort: z
    .string()
    .regex(/^[a-z0-9_]+$/)
    .max(64)
    .optional()
    .catch(undefined)
    .describe('The column id the rows are sorted by; absent is the report’s own order.'),
  order: z
    .enum(['asc', 'desc'])
    .optional()
    .catch(undefined)
    .describe('Sort direction; asc when a sort is set.'),
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('Page of the table, from 1; default 1.'),
  limit: z
    .union([z.literal(10), z.literal(25), z.literal(50), z.literal(100)])
    .optional()
    .catch(undefined)
    .describe('Rows per page; default 50.'),
})

export type ReconciliationSearch = z.infer<typeof reconciliationSearchSchema>

export const EMPTY_RECONCILIATION_SEARCH: ReconciliationSearch = reconciliationSearchSchema.parse(
  {},
)

/** Reset: every field cleared, so the page is as it first opened. */
export const RESET_RECONCILIATION: Partial<ReconciliationSearch> = Object.fromEntries(
  Object.keys(reconciliationSearchSchema.shape).map((key) => [key, undefined]),
)

/** Strip defaults and empties so a clean URL stays clean. */
export function withoutReconciliationDefaults(search: ReconciliationSearch): ReconciliationSearch {
  return pruned({
    ...search,
    limit: search.limit === DEFAULT_RECONCILIATION_PAGE_SIZE ? undefined : search.limit,
  })
}
